/**
 * Messages Service
 * API calls for message operations
 */

import apiClient from './api.client';
import type { ApiResponse, Message, MessageLink, ForwardMessageRequest } from '@/types/api.types';

export interface ListMessagesOptions {
  channelId?: string;
  dmThreadId?: string;
  limit?: number;
  before?: string; // Cursor: get messages before this ID (older)
  after?: string; // Cursor: get messages after this ID (newer)
}

export interface CreateMessageData {
  content: string;
  channelId?: string;
  dmThreadId?: string;
  replyToMessageId?: string;
  isVoiceMessage?: boolean;
  hasAttachments?: boolean;
}

/**
 * Paginated messages response with metadata for infinite scroll
 */
export interface PaginatedMessagesResponse {
  messages: Message[];
  hasOlderMessages: boolean;
  hasNewerMessages: boolean;
  oldestMessageId: string | null;
  newestMessageId: string | null;
}

/**
 * List messages in a channel or DM thread with pagination support
 * Returns paginated response with cursor metadata for infinite scroll
 */
export const listMessages = async (
  options: ListMessagesOptions
): Promise<PaginatedMessagesResponse> => {
  const { channelId, dmThreadId, limit = 50, before, after } = options;

  // Must specify either channelId or dmThreadId
  if (!channelId && !dmThreadId) {
    throw new Error('Either channelId or dmThreadId must be specified');
  }

  const params = new URLSearchParams({
    limit: limit.toString(),
  });

  // Add cursor parameters if provided
  if (before) {
    params.set('before', before);
  }
  if (after) {
    params.set('after', after);
  }

  let url: string;
  if (channelId) {
    url = `/channels/${channelId}/messages?${params.toString()}`;
  } else if (dmThreadId) {
    url = `/dm-threads/${dmThreadId}/messages?${params.toString()}`;
  } else {
    throw new Error('Either channelId or dmThreadId must be specified');
  }

  const response = await apiClient.get<
    ApiResponse<{
      messages: Message[];
      hasOlderMessages: boolean;
      hasNewerMessages: boolean;
      oldestMessageId: string | null;
      newestMessageId: string | null;
    }>
  >(url);

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch messages');
  }

  return {
    messages: response.data.data.messages,
    hasOlderMessages: response.data.data.hasOlderMessages,
    hasNewerMessages: response.data.data.hasNewerMessages,
    oldestMessageId: response.data.data.oldestMessageId,
    newestMessageId: response.data.data.newestMessageId,
  };
};

/**
 * Legacy listMessages that returns just Message[] for backward compatibility
 * Used by existing useMessages hook during transition
 */
export const listMessagesSimple = async (
  options: Omit<ListMessagesOptions, 'before' | 'after'>
): Promise<Message[]> => {
  const result = await listMessages(options);
  return result.messages;
};

/**
 * Create a new message
 */
export const createMessage = async (data: CreateMessageData): Promise<Message> => {
  // Must specify either channelId or dmThreadId
  if (!data.channelId && !data.dmThreadId) {
    throw new Error('Either channelId or dmThreadId must be specified');
  }

  const response = await apiClient.post<ApiResponse<{ message: Message }>>(
    '/messages',
    data
  );

  if (!response.data?.data?.message) {
    console.error('[createMessage] Invalid response structure:', response.data);
    throw new Error(response.data?.error || 'Failed to create message - invalid response');
  }

  return response.data.data.message;
};

/**
 * Update a message (edit content)
 */
export const updateMessage = async (
  messageId: string,
  content: string
): Promise<Message> => {
  const response = await apiClient.patch<ApiResponse<{ message: Message }>>(
    `/messages/${messageId}`,
    { content }
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to update message');
  }

  return response.data.data.message;
};

/**
 * Delete a message
 */
export const deleteMessage = async (messageId: string): Promise<void> => {
  const response = await apiClient.delete<ApiResponse>(`/messages/${messageId}`);

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to delete message');
  }
};

/**
 * Get a single message by ID
 */
export const getMessage = async (messageId: string): Promise<Message> => {
  const response = await apiClient.get<ApiResponse<{ message: Message }>>(
    `/messages/${messageId}`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch message');
  }

  return response.data.data.message;
};

/**
 * Get message thread (all replies to a message)
 */
export const getThread = async (messageId: string): Promise<Message[]> => {
  const response = await apiClient.get<ApiResponse<{ thread: Message[]; count: number }>>(
    `/messages/${messageId}/thread`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch thread');
  }

  return response.data.data.thread;
};

/**
 * Update link preview visibility (hide/show)
 * Only the message author can hide their link previews
 */
export const updateLinkVisibility = async (
  messageId: string,
  linkId: string,
  hidden: boolean
): Promise<MessageLink> => {
  const response = await apiClient.patch<ApiResponse<{ link: MessageLink }>>(
    `/messages/${messageId}/links/${linkId}`,
    { hidden }
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to update link visibility');
  }

  return response.data.data.link;
};

/**
 * Forward a message to a different channel or DM thread
 */
export const forwardMessage = async (
  messageId: string,
  data: ForwardMessageRequest
): Promise<Message> => {
  const response = await apiClient.post<ApiResponse<{ message: Message }>>(
    `/messages/${messageId}/forward`,
    data
  );

  if (!response.data?.data?.message) {
    throw new Error(response.data?.error || 'Failed to forward message');
  }

  return response.data.data.message;
};

// Export as a service object
export const messagesService = {
  listMessages,
  createMessage,
  updateMessage,
  deleteMessage,
  getMessage,
  getThread,
  updateLinkVisibility,
  forwardMessage,
};
